import { createSelector } from '@reduxjs/toolkit';
import { CLUSTER_CONSTANTS } from '../constants/clusterConstants';
import { EquipmentModel } from '../constants/equipmentConstants';
import { PHYTO_TASK_TYPE, PhytoTaskType } from '../constants/taskConstants';
import { NormalizedBeacon, selectBeaconsById } from '../slices/beacons';
import { Cluster, ClusterId, selectClustersById } from '../slices/clusters';
import { ColorItem } from '../slices/colors';
import { DeviceId, NormalizedDevice, selectDevicesById } from '../slices/devices';
import {
  EquipmentId,
  NormalizedEquipment,
  NormalizedEquipmentModel,
  selectEquipmentModelsById,
  selectEquipmentModelsIds,
  selectEquipmentsById,
  selectEquipmentsIds,
} from '../slices/equipments';
import { DeviceSessionId } from '../slices/sessions';
import { selectSearchFilter, selectSelectedClusters } from '../slices/ui';
import { UserId } from '../slices/users';
import { clusterContains, sortAlphaBeta } from '../utils';
import { getColorWithHash } from '../utils/colors';
import { buildActiveTracksFromEquipments, equipmentContains, isEquipmentArchived } from '../utils/equipments';
import { getTaskWorksiteTypes } from '../utils/tasks';
import { selectDriversWithCurrentBeaconWithAdminsAsArray } from './users';

export const selectEquipmentsAsArray = createSelector(
  [selectEquipmentsIds, selectEquipmentsById],
  (ids, equipmentById) => ids.map((id) => equipmentById[id]).filter((equipment) => !!equipment),
);

export const selectNonArchivedEquipmentsByClusterId = createSelector(
  [selectEquipmentsAsArray, (_state: unknown, clusterId: ClusterId | null) => clusterId],
  (equipments, clusterId) =>
    equipments
      .filter((equipment) => equipment.clusterId === clusterId && !isEquipmentArchived(equipment))
      .toSorted((a, b) => sortAlphaBeta(a.label, b.label)),
);

export const selectEquipmentModelsAsArray = createSelector(
  [selectEquipmentModelsIds, selectEquipmentModelsById],
  (ids, equipmentModelById) => ids.map((id) => equipmentModelById[id]).filter((model) => !!model),
);

export const selectEquipmentModelIdsByType = createSelector([selectEquipmentModelsAsArray], (models) =>
  Object.fromEntries(models.map((model) => [model.equipmentType, model.id])),
);

export const selectEquipmentColorItems = createSelector([selectEquipmentsAsArray], (equipments) =>
  equipments.flatMap((equipment): ColorItem[] =>
    equipment.color
      ? [{ id: String(equipment.id), name: equipment.label, color: getColorWithHash(equipment.color) }]
      : [],
  ),
);

export type VisibleEquipment = Omit<NormalizedEquipment, 'equipmentModel'> & {
  equipmentModel?: NormalizedEquipmentModel;
  hidden: boolean;
  activeDevice?: NormalizedDevice;
  activeBeacon?: NormalizedBeacon;
  cluster?: Cluster;
  latitude?: number;
  longitude?: number;
  locationDate?: string;
};

export const selectVisibleEquipments = createSelector(
  [
    selectSelectedClusters,
    selectSearchFilter,
    selectEquipmentsAsArray,
    selectEquipmentModelsById,
    selectClustersById,
    selectDevicesById,
    selectBeaconsById,
  ],
  (activeClusters, filter, equipments, equipmentModelById, clusterById, deviceById, beaconById) =>
    equipments
      .filter((equipment) => !activeClusters?.length || activeClusters.includes(equipment.clusterId))
      .map((equipment): VisibleEquipment => {
        const cluster = clusterById[equipment.clusterId];
        const device = equipment.currentDeviceId ? deviceById[equipment.currentDeviceId] : undefined;
        const beacon = equipment.currentBeaconUuid ? beaconById[equipment.currentBeaconUuid] : undefined;
        const geolocalizer = beacon ?? device;

        return {
          ...equipment,
          equipmentModel: equipmentModelById[equipment.equipmentModelId],
          hidden: !equipmentContains(equipment, filter) && !clusterContains(cluster, filter),
          activeDevice: device, // Should be renamed, not dependent on search
          activeBeacon: beacon,
          cluster,
          latitude: geolocalizer?.locationLatitude,
          longitude: geolocalizer?.locationLongitude,
          locationDate: geolocalizer?.locationDate,
        };
      }),
);

export const selectEquipmentData = createSelector(
  [(_state: unknown, id: EquipmentId | string) => id, selectVisibleEquipments],
  (id, visibleEquipments) => visibleEquipments.find((equipment) => equipment.id === Number(id)) ?? null,
);

export const selectEquipmentActiveTracks = createSelector([selectVisibleEquipments], (equipments) =>
  buildActiveTracksFromEquipments(equipments),
);

type ActiveWorksiteEquipment = Partial<NormalizedEquipmentModel> & {
  equipmentModel: { type?: EquipmentModel };
  label: string;
  id: EquipmentId;
};

type ActiveWorksiteUserSession = {
  user_id: UserId;
  user_fullname?: string;
  user_email?: string;
  device_instance_id?: DeviceId;
  from_date?: string;
  device_session_uuid?: DeviceSessionId;
};

type ActiveEquipmentWorksite = {
  equipment_instances: ActiveWorksiteEquipment[];
  equipment_model?: EquipmentModel;
  from_date: string;
  cluster_id: ClusterId;
  device_session_id: DeviceSessionId;
  equipmentLatitude?: number;
  equipmentLongitude?: number;
  device_session_status: 'active';
  related_user_sessions?: ActiveWorksiteUserSession[];
};

const asWorksiteEquipment = (equipment: VisibleEquipment): ActiveWorksiteEquipment => ({
  ...equipment.equipmentModel,
  equipmentModel: { type: equipment.equipmentModel?.type },
  label: equipment.label,
  id: equipment.id,
});

export const selectActiveEquipmentsAsWorksites = createSelector(
  [selectVisibleEquipments, selectDriversWithCurrentBeaconWithAdminsAsArray],
  (equipments, drivers) => {
    const activeDrivers = (drivers || []).filter((driver) => driver.status === 'IN_USE');
    const worksiteBySessionId: Record<DeviceSessionId, ActiveEquipmentWorksite> = {};

    for (const equipment of equipments) {
      const { activeSession } = equipment;
      if (equipment.status !== 'IN_USE' || !activeSession) {
        continue;
      }
      const existing = worksiteBySessionId[activeSession.parentDeviceSessionId];
      if (existing) {
        existing.equipment_instances.push(asWorksiteEquipment(equipment));
        // a mixed worksite is named after the implement rather than the tractor pulling it
        if (existing.equipment_model === 'tractor' && equipment.equipmentModel?.type !== 'tractor') {
          existing.equipment_model = equipment.equipmentModel?.type;
        }
        continue;
      }

      const relatedDrivers = activeDrivers.filter(
        (driver) => driver.activeSession?.parentDeviceSessionId === activeSession.parentDeviceSessionId,
      );
      worksiteBySessionId[activeSession.parentDeviceSessionId] = {
        equipment_instances: [asWorksiteEquipment(equipment)],
        equipment_model: equipment.equipmentModel?.type,
        from_date: activeSession.fromDate,
        cluster_id: equipment.clusterId,
        device_session_id: activeSession.deviceSessionId,
        equipmentLatitude: equipment.latitude,
        equipmentLongitude: equipment.longitude,
        device_session_status: 'active',
        ...(relatedDrivers.length
          ? {
              related_user_sessions: relatedDrivers.map((driver) => ({
                user_id: driver.id,
                user_fullname: driver.fullname,
                user_email: driver.email,
                device_instance_id: driver.activeSession?.deviceInstanceId,
                from_date: driver.activeSession?.fromDate,
                device_session_uuid: driver.activeSession?.deviceSessionId,
              })),
            }
          : {}),
      };
    }

    return Object.values(worksiteBySessionId);
  },
);

const selectEquipmentsForTasksOfType = (taskType: PhytoTaskType) =>
  createSelector(
    [selectEquipmentsAsArray, selectEquipmentModelsById, selectClustersById],
    (equipments, equipmentModelsById, clustersById) =>
      equipments
        .filter((equipment) => {
          const clusterHasPlugin =
            (clustersById[equipment.clusterId]?.plugins || []).find(
              (plugin) => plugin.option === CLUSTER_CONSTANTS.PLUGINS_NAMES.phyto,
            )?.activated || false;
          const equipmentModel = equipmentModelsById[equipment.equipmentModelId]?.type;
          return clusterHasPlugin && !!equipmentModel && getTaskWorksiteTypes(taskType).includes(equipmentModel);
        })
        .toSorted((a, b) => sortAlphaBeta(a.label, b.label)),
  );

export const selectSpecificEquipmentsForTasks = {
  [PHYTO_TASK_TYPE.PHYTO]: selectEquipmentsForTasksOfType(PHYTO_TASK_TYPE.PHYTO),
  [PHYTO_TASK_TYPE.SEMENCE]: selectEquipmentsForTasksOfType(PHYTO_TASK_TYPE.SEMENCE),
};

export const selectSpecificEquipmentsForAllPhytoTaskTypes = createSelector(
  [selectSpecificEquipmentsForTasks[PHYTO_TASK_TYPE.PHYTO], selectSpecificEquipmentsForTasks[PHYTO_TASK_TYPE.SEMENCE]],
  (phytoEquipments, seedEquipments) =>
    phytoEquipments.concat(seedEquipments).toSorted((a, b) => sortAlphaBeta(a.label, b.label)),
);
